import { useState, useEffect, useRef } from 'react'
import { X, Copy, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useBuilderPages, generateSlug } from './hooks/useBuilderPages'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'

/**
 * Modale de duplication d'une page builder.
 *
 * Pré-remplit le titre avec "Copie de {page.title}".
 * Affiche le slug généré en temps réel.
 * Appelle POST /api/builder/page/duplicate via duplicatePage(),
 * puis redirige vers /builder/{newSlug}.
 */
export const BuilderDuplicateModal = ({ page, onClose }) => {
  const scrim = useScrimDismiss(onClose)
  const { duplicatePage } = useBuilderPages()
  const navigate = useNavigate()

  const defaultTitle = `Copy of ${page.title || page.slug}`
  const [title, setTitle] = useState(defaultTitle)
  const [duplicating, setDuplicating] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  // Focus + sélection du texte à l'ouverture
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  useEscapeClose(onClose)

  const slug = generateSlug(title.trim() || defaultTitle)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = title.trim()
    if (!trimmed) return

    setDuplicating(true)
    setError(null)
    try {
      const newSlug = await duplicatePage(page.slug, trimmed)
      onClose()
      navigate(`/builder/${newSlug}?edit=1`)
    } catch (err) {
      setError(err.message || 'An error occurred.')
    } finally {
      setDuplicating(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      {...scrim}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center">
              <Copy size={14} className="text-teal-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-800">Duplicate page</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-5">

          {/* Page source (lecture seule) */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
            <span className="text-xs text-gray-400">Source:</span>
            <span className="text-xs font-medium text-gray-600 truncate">{page.title || page.slug}</span>
            <span className="text-xs font-mono text-gray-300 truncate ml-auto">/builder/{page.slug}</span>
          </div>

          {/* Titre de la copie */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              New page title
            </label>
            <input
              ref={inputRef}
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={defaultTitle}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
            />
            {/* Aperçu slug */}
            <p className="mt-1.5 text-xs text-gray-400 font-mono truncate">
              /builder/<span className="text-gray-600">{slug}</span>
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || duplicating}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {duplicating
                ? <><Loader2 size={14} className="animate-spin" />Duplicating…</>
                : <><Copy size={14} />Duplicate</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
